import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { BACKEND_URL } from '../config';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Wrench, Briefcase, Heart, Music, CheckSquare, Calendar as CalendarIcon, Repeat, Plane, PartyPopper, GraduationCap, Stethoscope } from 'lucide-react';

const TASK_TYPES = [
  {
    value: 'montaje',
    label: 'Montaje de equipo',
    icon: Wrench,
    color: '#f59e0b' // amber
  },
  {
    value: 'cita_profesional',
    label: 'Cita profesional',
    icon: Briefcase,
    color: '#3b82f6' // blue
  },
  {
    value: 'cita_personal',
    label: 'Cita personal',
    icon: Heart,
    color: '#ec4899' // pink
  },
  {
    value: 'ensayo',
    label: 'Ensayo',
    icon: Music,
    color: '#8b5cf6' // purple
  },
  {
    value: 'tarea_general',
    label: 'Tarea general',
    icon: CheckSquare,
    color: '#10b981' // emerald
  },
  {
    value: 'viaje',
    label: 'Viaje / Vacaciones',
    icon: Plane,
    color: '#06b6d4' // cyan
  },
  {
    value: 'evento_personal',
    label: 'Evento personal (cumple, boda...)',
    icon: PartyPopper,
    color: '#f472b6' // pink-400
  },
  {
    value: 'formacion',
    label: 'Formación / Curso',
    icon: GraduationCap,
    color: '#a78bfa' // violet-400
  },
  {
    value: 'medico',
    label: 'Cita médica',
    icon: Stethoscope,
    color: '#ef4444' // red
  }
];

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: '📅 Diario', description: 'Cada día' },
  { value: 'weekly', label: '🔁 Semanal', description: 'Cada 7 días' },
  { value: 'biweekly', label: '📆 Quincenal', description: 'Cada 14 días' },
  { value: 'monthly', label: '📅 Mensual', description: 'Cada mes (mismo día)' },
  { value: 'yearly', label: '🎂 Anual', description: 'Cada año (mismo día)' }
];

const SERIES_MODE_LABELS = {
  only_this: 'Solo esta instancia',
  this_and_future: 'Esta y futuras',
  all: 'Toda la serie'
};

export const TaskForm = ({ isOpen, onClose, onSubmit, onDelete, initialData = null, seriesEditMode = null }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    task_type: initialData?.task_type || 'tarea_general',
    notes: initialData?.notes || '',
    start_datetime: initialData?.start_datetime 
      ? initialData.start_datetime.slice(0, 16)
      : '',
    end_datetime: initialData?.end_datetime 
      ? initialData.end_datetime.slice(0, 16)
      : '',
    all_day: initialData?.all_day || false,
    is_multi_day: initialData?.is_multi_day || false,
    assigned_to_user_id: initialData?.assigned_to_user_id || '',
    color: initialData?.color || null,
    priority: initialData?.priority || 'medium',
    is_recurring: initialData?.is_recurring || false,
    recurrence_frequency: initialData?.recurrence_frequency || 'weekly',
    recurrence_end_date: initialData?.recurrence_end_date 
      ? initialData.recurrence_end_date.slice(0, 10)
      : ''
  });

  const [users, setUsers] = useState([]);
  
  // Determinar si estamos editando una tarea de serie
  const isEditingSeriesTask = initialData && (initialData.is_recurring || initialData.parent_task_id);

  // Cargar usuarios activos
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data || []);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  // Actualizar el formulario cuando cambie initialData (al editar)
  useEffect(() => {
    if (initialData) {
      let datetimeLocal = '';
      let endDatetimeLocal = '';
      if (initialData.start_datetime) {
        datetimeLocal = initialData.start_datetime.slice(0, 16);
      }
      if (initialData.end_datetime) {
        endDatetimeLocal = initialData.end_datetime.slice(0, 16);
      }
      
      setFormData({
        title: initialData.title || '',
        task_type: initialData.task_type || 'tarea_general',
        notes: initialData.notes || '',
        start_datetime: datetimeLocal,
        end_datetime: endDatetimeLocal,
        all_day: initialData.all_day || false,
        is_multi_day: initialData.is_multi_day || false,
        assigned_to_user_id: initialData.assigned_to_user_id || '',
        color: initialData.color || null,
        priority: initialData.priority || 'medium',
        is_recurring: initialData.is_recurring || false,
        recurrence_frequency: initialData.recurrence_frequency || 'weekly',
        recurrence_end_date: initialData.recurrence_end_date 
          ? initialData.recurrence_end_date.slice(0, 10)
          : ''
      });
    } else {
      setFormData({
        title: '',
        task_type: 'tarea_general',
        notes: '',
        start_datetime: '',
        end_datetime: '',
        all_day: false,
        is_multi_day: false,
        assigned_to_user_id: '',
        color: null,
        priority: 'medium',
        is_recurring: false,
        recurrence_frequency: 'weekly',
        recurrence_end_date: ''
      });
    }
  }, [initialData]);

  // Auto-activar multi-day para tipos de tarea que suelen durar varios días
  useEffect(() => {
    if (formData.task_type === 'viaje') {
      setFormData(prev => ({ ...prev, is_multi_day: true, all_day: true }));
    }
  }, [formData.task_type]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('El título es obligatorio');
      return;
    }
    
    if (!formData.start_datetime) {
      alert('La fecha de inicio es obligatoria');
      return;
    }

    if (formData.is_multi_day && !formData.end_datetime) {
      alert('Para eventos de varios días, la fecha de fin es obligatoria');
      return;
    }
    
    const payload = {
      ...formData,
      start_datetime: formData.start_datetime + ':00.000Z',
      end_datetime: formData.is_multi_day && formData.end_datetime 
        ? formData.end_datetime + ':00.000Z' 
        : null,
      color: formData.color || TASK_TYPES.find(t => t.value === formData.task_type)?.color,
      recurrence_end_date: formData.is_recurring && formData.recurrence_end_date 
        ? formData.recurrence_end_date + 'T23:59:59.000Z' 
        : null,
      assigned_to_user_id: formData.assigned_to_user_id || null
    };
    
    onSubmit(payload);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const selectedTaskType = TASK_TYPES.find(t => t.value === formData.task_type);
  const TaskIcon = selectedTaskType?.icon || CheckSquare;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <TaskIcon className="w-5 h-5" style={{ color: selectedTaskType?.color }} />
            {initialData ? 'Editar Tarea' : 'Nueva Tarea'}
          </DialogTitle>
          {/* Indicador de edición de serie */}
          {isEditingSeriesTask && seriesEditMode && (
            <div className="mt-2 bg-purple-900/30 border border-purple-600/40 rounded-lg px-3 py-2">
              <p className="text-sm text-purple-300 flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                <span>
                  Editando: <strong>{SERIES_MODE_LABELS[seriesEditMode]}</strong>
                </span>
              </p>
              {seriesEditMode !== 'only_this' && (
                <p className="text-xs text-purple-400 mt-1">
                  Los cambios de hora se aplicarán a {seriesEditMode === 'all' ? 'todas las instancias' : 'esta y todas las instancias futuras'}
                </p>
              )}
            </div>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-slate-200">
              Título *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Ej: Montar equipo Hotel Meliá"
              className="bg-slate-800 border-slate-600 text-white"
              required
            />
          </div>

          {/* Tipo de tarea */}
          <div className="space-y-2">
            <Label htmlFor="task_type" className="text-slate-200">
              Tipo de tarea *
            </Label>
            <Select
              value={formData.task_type}
              onValueChange={(value) => handleChange('task_type', value)}
            >
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 max-h-[300px]">
                {TASK_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <SelectItem 
                      key={type.value} 
                      value={type.value}
                      className="text-white hover:bg-slate-700"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: type.color }} />
                        {type.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Evento de varios días */}
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-cyan-400" />
              <Label htmlFor="is_multi_day" className="text-slate-200">
                Evento de varios días
              </Label>
            </div>
            <Switch
              id="is_multi_day"
              checked={formData.is_multi_day}
              onCheckedChange={(checked) => handleChange('is_multi_day', checked)}
            />
          </div>

          {/* Fechas */}
          <div className={`grid gap-4 ${formData.is_multi_day ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <div className="space-y-2">
              <Label htmlFor="start_datetime" className="text-slate-200">
                {formData.is_multi_day ? 'Fecha inicio *' : 'Fecha y hora *'}
              </Label>
              <Input
                id="start_datetime"
                type={formData.is_multi_day && formData.all_day ? 'date' : 'datetime-local'}
                value={formData.is_multi_day && formData.all_day 
                  ? formData.start_datetime.slice(0, 10) 
                  : formData.start_datetime}
                onChange={(e) => handleChange('start_datetime', 
                  formData.is_multi_day && formData.all_day 
                    ? e.target.value + 'T00:00' 
                    : e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                required
              />
            </div>
            
            {formData.is_multi_day && (
              <div className="space-y-2">
                <Label htmlFor="end_datetime" className="text-slate-200">
                  Fecha fin *
                </Label>
                <Input
                  id="end_datetime"
                  type={formData.all_day ? 'date' : 'datetime-local'}
                  value={formData.all_day 
                    ? (formData.end_datetime ? formData.end_datetime.slice(0, 10) : '')
                    : formData.end_datetime}
                  onChange={(e) => handleChange('end_datetime', 
                    formData.all_day 
                      ? e.target.value + 'T23:59' 
                      : e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white"
                  min={formData.start_datetime ? formData.start_datetime.slice(0, 10) : ''}
                  required={formData.is_multi_day}
                />
              </div>
            )}
          </div>

          {/* Info de duración para eventos multi-día */}
          {formData.is_multi_day && formData.start_datetime && formData.end_datetime && (
            <div className="p-3 bg-cyan-900/30 rounded-lg border border-cyan-700/50">
              <p className="text-cyan-300 text-sm flex items-center gap-2">
                <Plane className="w-4 h-4" />
                Duración: {(() => {
                  const start = new Date(formData.start_datetime);
                  const end = new Date(formData.end_datetime);
                  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                  return `${days} día${days !== 1 ? 's' : ''}`;
                })()}
              </p>
            </div>
          )}

          {/* Tarea recurrente */}
          {!initialData && !formData.is_multi_day && (
            <div className="space-y-3 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-purple-400" />
                  <Label htmlFor="is_recurring" className="text-slate-200">
                    Tarea recurrente
                  </Label>
                </div>
                <Switch
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) => handleChange('is_recurring', checked)}
                />
              </div>
              
              {formData.is_recurring && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="recurrence_frequency" className="text-slate-200 text-sm">
                      Frecuencia
                    </Label>
                    <Select
                      value={formData.recurrence_frequency}
                      onValueChange={(value) => handleChange('recurrence_frequency', value)}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        {RECURRENCE_OPTIONS.map((option) => (
                          <SelectItem 
                            key={option.value} 
                            value={option.value}
                            className="text-white hover:bg-slate-700"
                          >
                            <div className="flex flex-col">
                              <span>{option.label}</span>
                              <span className="text-xs text-slate-400">{option.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="recurrence_end_date" className="text-slate-200 text-sm">
                      Repetir hasta (opcional)
                    </Label>
                    <Input
                      id="recurrence_end_date"
                      type="date"
                      value={formData.recurrence_end_date}
                      onChange={(e) => handleChange('recurrence_end_date', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white"
                      min={formData.start_datetime ? formData.start_datetime.slice(0, 10) : ''}
                    />
                    <p className="text-xs text-slate-400">
                      {formData.recurrence_frequency === 'daily' && 'Máx: 365 repeticiones (1 año)'}
                      {formData.recurrence_frequency === 'weekly' && 'Máx: 52 repeticiones (1 año)'}
                      {formData.recurrence_frequency === 'biweekly' && 'Máx: 26 repeticiones (1 año)'}
                      {formData.recurrence_frequency === 'monthly' && 'Máx: 24 repeticiones (2 años)'}
                      {formData.recurrence_frequency === 'yearly' && 'Máx: 10 repeticiones (10 años)'}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Prioridad */}
          <div className="space-y-2">
            <Label htmlFor="priority" className="text-slate-200">
              Prioridad
            </Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => handleChange('priority', value)}
            >
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="low" className="text-white hover:bg-slate-700">
                  🟢 Baja
                </SelectItem>
                <SelectItem value="medium" className="text-white hover:bg-slate-700">
                  🟡 Media
                </SelectItem>
                <SelectItem value="high" className="text-white hover:bg-slate-700">
                  🟠 Alta
                </SelectItem>
                <SelectItem value="urgent" className="text-white hover:bg-slate-700">
                  🔴 Urgente
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Asignar a usuario */}
          <div className="space-y-2">
            <Label htmlFor="assigned_to_user_id" className="text-slate-200">
              Asignar a
            </Label>
            <Select
              value={formData.assigned_to_user_id || "none"}
              onValueChange={(value) => handleChange('assigned_to_user_id', value === "none" ? "" : value)}
            >
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Sin asignar (tarea propia)" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="none" className="text-white hover:bg-slate-700">
                  Sin asignar (tarea propia)
                </SelectItem>
                {users.map((user) => (
                  <SelectItem 
                    key={user.id} 
                    value={user.id}
                    className="text-white hover:bg-slate-700"
                  >
                    👤 {user.username} {user.role === 'admin' ? '(Admin)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-slate-200">
              Notas
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Detalles, ubicación, equipo necesario, etc."
              className="bg-slate-800 border-slate-600 text-white min-h-[80px]"
              rows={3}
            />
          </div>

          <DialogFooter className="flex justify-between items-center">
            <div>
              {initialData && onDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    if (window.confirm('¿Estás seguro de eliminar esta tarea?')) {
                      onDelete(initialData.id);
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Eliminar Tarea
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {initialData ? 'Guardar Cambios' : 'Crear Tarea'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { TASK_TYPES };
